import { useCallback, useEffect, useRef, useState } from 'react';
import type { YMapsApi } from 'react-yandex-maps';
import { DEFAULT_MAP_CENTER } from '@constants/geo';
import { requireOptionalMapModule } from '@constants/map';
import API from '@api';
import DadataAPI from '@api/dadata';
import { creatStandartDadataObj } from '@utils/address';
import { geoLacation3x } from '@images';
import type {
  BalloonState,
  AddressFormFields
} from '@customHooks/useAddressForm';
import type { DadataSuggestion } from '@api/dadata';
import type { YMapRef, YMapsMapEvent } from '../../types/yandex';

export interface AddressAcceptedData {
  address: string;
  dadataId: string;
  coords: number[];
  isShowMapMarkPlace: boolean;
  balloonState: BalloonState;
  source: 'daData' | 'mapClick' | 'balloon';
}

export interface BalloonCreatedData {
  dadataId: string;
  balloonState: BalloonState;
  coords: number[];
}

interface UseAddressMapOptions {
  setFields: (partial: Partial<AddressFormFields>) => void;
  onAfterAddressAccepted?: (data: AddressAcceptedData) => void;
  onAfterBalloonCreated?: (data: BalloonCreatedData) => void;
  onError?: (error: Error) => void;
  isEditMode: () => boolean;
  noGeoLocation?: boolean;
}

export interface AddressMapReturn {
  onSelectDaData: (
    suggestion: DadataSuggestion,
    coords: number[],
    dadataId: string | number,
    addDadata: any
  ) => void;
  onMapClick: (e: YMapsMapEvent, mapName: string) => void;
  createBalloon: (coords: number[], mapRef: YMapRef) => void;
  clickBalloon: (e: MouseEvent | CustomEvent) => void;
  createTemplateLayoutFactory: (ymaps: YMapsApi) => void;
  isDefaultMark: (coords: number[]) => boolean;
  getGeoLocation: (ymaps: YMapsApi) => void;
  ymapsRef: React.MutableRefObject<YMapsApi | null>;
  iconContentTemplateRef: React.MutableRefObject<unknown>;
  iconContentTemplate: unknown;
  mapRefs: React.MutableRefObject<Record<string, YMapRef | null>>;
  balloonStateRef: React.MutableRefObject<BalloonState>;
}

export const useAddressMap = ({
  setFields,
  onAfterAddressAccepted,
  onAfterBalloonCreated,
  onError,
  isEditMode,
  noGeoLocation = false
}: UseAddressMapOptions): AddressMapReturn => {
  const ymapsRef = useRef<YMapsApi | null>(null);
  const iconContentTemplateRef = useRef<unknown>(null);
  const mapRefs = useRef<Record<string, YMapRef | null>>({});
  const balloonStateRef = useRef<BalloonState>({});
  const [iconContentTemplate, setIconContentTemplate] = useState<unknown>(null);

  const handleAddressAccepted = useCallback(
    (data: AddressAcceptedData) => {
      const {
        address: addr,
        dadataId: did,
        coords: newCoords,
        isShowMapMarkPlace: showMark,
        balloonState: bs
      } = data;
      setFields({
        ...(addr ? { address: addr } : {}),
        ...(did ? { dadataId: Number(did) } : {}),
        ...(newCoords.length ? { coords: newCoords } : {}),
        isShowMapMarkPlace: showMark,
        balloonState: bs
      });
      onAfterAddressAccepted?.(data);
    },
    [setFields, onAfterAddressAccepted]
  );

  const handleBalloonCreated = useCallback(
    (data: BalloonCreatedData) => {
      const { balloonState: bs, coords: bCoords } = data;
      setFields({
        dadataId: null,
        balloonState: bs,
        ...(bs.address ? { address: bs.address } : {}),
        ...(bCoords?.length ? { coords: bCoords } : {})
      });
      onAfterBalloonCreated?.(data);
    },
    [setFields, onAfterBalloonCreated]
  );

  const createBalloon = useCallback((coords: number[], mapRef: YMapRef) => {
    if (!mapRef?.balloon) {
      return;
    }
    mapRef.balloon.open(coords, {
      contentBody: `
        <div class="ymaps-baloon">
          <div class="ymaps-baloon-title">
            Это ваш адрес?
          </div>
          <div class="ymaps-baloon-content ymaps-confirm-address-content">
            <button class="button js-order-confirm-address">
              Да
            </button>
            <button class="button button--ghost js-order-reject-address">
              Указать на карте
            </button>
          </div>
        </div>
      `
    });
  }, []);

  const getActiveMapRef = useCallback(
    (preferredMap?: string): YMapRef | null => {
      if (preferredMap && mapRefs.current[preferredMap]) {
        return mapRefs.current[preferredMap];
      }
      if (mapRefs.current.mapRef) return mapRefs.current.mapRef;
      if (mapRefs.current.mapRefModal) return mapRefs.current.mapRefModal;
      return null;
    },
    []
  );

  const getActiveMapName = useCallback((): string | null => {
    if (mapRefs.current.mapRefModal) return 'mapRefModal';
    if (mapRefs.current.mapRef) return 'mapRef';
    return null;
  }, []);

  const onSelectDaData = useCallback(
    (
      suggestion: DadataSuggestion,
      coords: number[],
      dadataId: string | number,
      addDadata: any
    ) => {
      const { qc_geo } = suggestion.data ?? {};
      const dadataIdStr = String(dadataId);
      const mapName = getActiveMapName();
      const mapRef = getActiveMapRef(mapName || undefined);

      if (
        qc_geo !== null &&
        qc_geo !== undefined &&
        +qc_geo >= 2 &&
        !addDadata?.dadataItem?.is_fixed
      ) {
        if (mapRef) createBalloon(coords, mapRef);
        const balloonState: BalloonState = {
          address: suggestion.value ?? '',
          dadataId: dadataIdStr,
          map: mapName ?? null
        };
        balloonStateRef.current = balloonState;
        handleBalloonCreated({
          dadataId: '',
          balloonState,
          coords
        });
      } else {
        if (mapRef?.balloon) {
          mapRef.balloon.close();
        }
        balloonStateRef.current = {};
        handleAddressAccepted({
          address: suggestion.value ?? '',
          dadataId: dadataIdStr,
          coords,
          isShowMapMarkPlace: true,
          balloonState: {},
          source: 'daData'
        });
      }
    },
    [
      createBalloon,
      getActiveMapRef,
      getActiveMapName,
      handleAddressAccepted,
      handleBalloonCreated
    ]
  );

  const onMapClick = useCallback(
    (e: YMapsMapEvent, mapName: string) => {
      if (!isEditMode()) return;

      const coords = e.get('coords');
      const mapRef = getActiveMapRef(mapName);

      DadataAPI.getMapSuggestionsGeolocate(String(coords[0]), String(coords[1]))
        .then((res: { suggestions: DadataSuggestion[] }) => res.suggestions[0])
        .then((suggestion: DadataSuggestion) => {
          const { qc_geo } = suggestion.data ?? {};
          const address = suggestion?.value ?? '';

          API.addDadata({
            input: creatStandartDadataObj(suggestion.data)
          })
            .then(async (res: any) => {
              if (!res.addDadata.result && res.addDadata.error) {
                const error = new Error(`${res.addDadata.error} ${address}`);
                return await Promise.reject(error);
              }

              if (
                qc_geo !== null &&
                qc_geo !== undefined &&
                +qc_geo >= 2 &&
                !res.addDadata?.dadataItem?.is_fixed
              ) {
                if (mapRef) createBalloon(coords, mapRef);
                const balloonState: BalloonState = {
                  address,
                  dadataId: String(res.addDadata.dadataItem.id ?? ''),
                  map: mapName ?? null
                };
                balloonStateRef.current = balloonState;
                handleBalloonCreated({
                  dadataId: '',
                  balloonState,
                  coords
                });
              } else {
                if (mapRef?.balloon) {
                  mapRef.balloon.close();
                }
                balloonStateRef.current = {};
                handleAddressAccepted({
                  address,
                  dadataId: String(res.addDadata.dadataItem.id ?? ''),
                  coords,
                  isShowMapMarkPlace: true,
                  balloonState: {},
                  source: 'mapClick'
                });
              }
            })
            .catch((error: Error) => {
              onError?.(error);
            });
        });
    },
    [
      isEditMode,
      getActiveMapRef,
      createBalloon,
      handleAddressAccepted,
      handleBalloonCreated,
      onError
    ]
  );

  const clickBalloon = useCallback(
    (e: MouseEvent | CustomEvent) => {
      let target = e.target as HTMLElement | null;
      if (e.type === 'balloonClick' && 'detail' in e) {
        target = e.detail;
      }
      if (!target || typeof target.closest !== 'function') return;

      const confirmButton = target.closest('.js-order-confirm-address');
      const rejectButton = target.closest('.js-order-reject-address');
      if (!confirmButton && !rejectButton) return;

      const { address = '', dadataId = '', map } = balloonStateRef.current;
      if (!address && !dadataId && !map) return;

      e.preventDefault();
      e.stopPropagation();

      const mapRef = getActiveMapRef(map || undefined);
      if (mapRef?.balloon) {
        mapRef.balloon.close();
      }

      if (rejectButton) {
        handleAddressAccepted({
          address: '',
          dadataId: '',
          coords: [],
          isShowMapMarkPlace: false,
          balloonState: balloonStateRef.current,
          source: 'balloon'
        });
        return;
      }

      balloonStateRef.current = {};
      handleAddressAccepted({
        address: address || '',
        dadataId: dadataId || '',
        coords: [],
        isShowMapMarkPlace: true,
        balloonState: {},
        source: 'balloon'
      });
    },
    [getActiveMapRef, handleAddressAccepted]
  );

  useEffect(() => {
    document.addEventListener('click', clickBalloon, true);
    document.addEventListener('balloonClick', clickBalloon);
    return () => {
      document.removeEventListener('click', clickBalloon, true);
      document.removeEventListener('balloonClick', clickBalloon);
    };
  }, [clickBalloon]);

  // Геолокация — опциональная фича: модуль 'geolocation' больше не входит в
  // обязательные modules карты (см. комментарий в @constants/map), поэтому
  // подгружаем его лениво. Любая ошибка (лимиты Локатора, отказ пользователя
  // в браузерной геолокации и т.п.) молча игнорируется и не ломает карту.
  const getGeoLocation = useCallback((ymaps: YMapsApi) => {
    const applyGeoLocationMark = (geolocation: any) => {
      try {
        geolocation
          .get({ provider: 'browser', mapStateAutoApply: false })
          .then((result: any) => {
            result.geoObjects.options.set({
              iconLayout: 'default#image',
              iconImageSize: [24, 24],
              iconImageOffset: [-12, -24],
              iconImageHref: geoLacation3x
            });
          })
          .catch(() => {});
      } catch {
        // опциональная фича — игнорируем
      }
    };

    if (ymaps?.geolocation?.get) {
      applyGeoLocationMark(ymaps.geolocation);
      return;
    }
    requireOptionalMapModule(ymaps, 'geolocation').then(geolocationModule => {
      const geolocation = ymaps?.geolocation?.get
        ? ymaps.geolocation
        : geolocationModule;
      if (geolocation?.get) applyGeoLocationMark(geolocation);
    });
  }, []);

  const createTemplateLayoutFactory = useCallback(
    (ymaps: YMapsApi) => {
      if (!noGeoLocation) getGeoLocation(ymaps);
      ymapsRef.current = ymaps;

      if (ymaps?.templateLayoutFactory && !iconContentTemplateRef.current) {
        iconContentTemplateRef.current = () =>
          ymaps.templateLayoutFactory.createClass(
            '<div style="color: #FFFFFF; font-weight: bold;">$[properties.iconContent]</div>'
          );
        setIconContentTemplate(iconContentTemplateRef.current);
      }
    },
    [getGeoLocation, noGeoLocation]
  );

  const isDefaultMark = useCallback(
    (coords: number[]) =>
      coords[0] === DEFAULT_MAP_CENTER[0] &&
      coords[1] === DEFAULT_MAP_CENTER[1],
    []
  );

  return {
    onSelectDaData,
    onMapClick,
    createBalloon,
    clickBalloon,
    createTemplateLayoutFactory,
    isDefaultMark,
    getGeoLocation,
    ymapsRef,
    iconContentTemplateRef,
    iconContentTemplate,
    mapRefs,
    balloonStateRef
  };
};

export default useAddressMap;
